/**
 * Tema Werc: os temas "Macro Light/Dark" (copiados, congelados) com o laranja da
 * marca Werc (#E1592A) como cor de destaque. O motor do Macro (`themeCssVars`)
 * transforma o tema em variáveis CSS; o atributo `data-theme-light` no <html>
 * liga os materiais claro/escuro do CSS do Macro.
 *
 * Preferência: "claro" | "escuro" | "sistema" (padrão: segue o sistema operacional).
 * Guardada no navegador por conveniência; sem armazenamento, segue o sistema.
 */
import { createSignal } from 'solid-js'
import { macroDarkTheme } from '@theme/themes/macro-dark'
import { macroLightTheme } from '@theme/themes/macro-light'
import { themeCssVars } from '@theme/utils/themeCssVars'
import type { ThemeV3 } from '@theme/types/themeTypes'

const LARANJA_WERC = 'oklch(63.47% 0.1799 38.34)' // #E1592A
const LARANJA_WERC_ESCURO = 'oklch(69.47% 0.1799 38.34)' // mais claro para fundo escuro

export const wercClaro = {
  ...macroLightTheme,
  id: 'Werc Claro',
  name: 'Werc Claro',
  colorTokens: { ...macroLightTheme.colorTokens, accent: LARANJA_WERC },
} satisfies ThemeV3

export const wercEscuro = {
  ...macroDarkTheme,
  id: 'Werc Escuro',
  name: 'Werc Escuro',
  colorTokens: { ...macroDarkTheme.colorTokens, accent: LARANJA_WERC_ESCURO },
} satisfies ThemeV3

export type PreferenciaTema = 'claro' | 'escuro' | 'sistema'
const CHAVE = 'werc.tema'

function lerPreferencia(): PreferenciaTema {
  try {
    const v = localStorage.getItem(CHAVE)
    if (v === 'claro' || v === 'escuro' || v === 'sistema') return v
  } catch {
    /* sem armazenamento: segue o sistema */
  }
  return 'sistema'
}

const sistemaEscuro = () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches

const [preferencia, setPreferenciaSinal] = createSignal<PreferenciaTema>(lerPreferencia())
export { preferencia }

export function modoEfetivo(p: PreferenciaTema = preferencia()): 'claro' | 'escuro' {
  return p === 'sistema' ? (sistemaEscuro() ? 'escuro' : 'claro') : p
}

let varsAplicadas: string[] = []
export function aplicarTema(p: PreferenciaTema = preferencia()): void {
  const modo = modoEfetivo(p)
  const tema = modo === 'escuro' ? wercEscuro : wercClaro
  const raiz = document.documentElement
  for (const k of varsAplicadas) raiz.style.removeProperty(k)
  const vars = themeCssVars(tema)
  for (const [k, v] of Object.entries(vars)) raiz.style.setProperty(k, v)
  varsAplicadas = Object.keys(vars)
  raiz.dataset.themeLight = modo === 'claro' ? 'true' : 'false'
  raiz.style.colorScheme = modo === 'claro' ? 'light' : 'dark'
}

export function setPreferencia(p: PreferenciaTema): void {
  setPreferenciaSinal(p)
  try {
    localStorage.setItem(CHAVE, p)
  } catch {
    /* ignora */
  }
  aplicarTema(p)
}

/** Liga o tema e acompanha a troca claro/escuro do sistema quando a preferência é "sistema". */
export function iniciarTema(): void {
  aplicarTema()
  if (typeof matchMedia === 'function') {
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (preferencia() === 'sistema') aplicarTema()
    })
  }
}
